import { action, computed, observable, runInAction } from 'mobx';
import { message } from 'antd';
import type { ProjectResult } from '../../common/types';
import { ProjectsService } from '../../common/services';
import { UsersStore } from '../../common/stores';
import type { ApiKey, ApiKeyInput, ApiKeyScope, CreateApiKeyInput } from '../types';
import { GlobalAdministrationService } from '../service/GlobalAdministrationService';

export class ApiKeysStore {
    @observable
    apiKeys: ApiKey[] = [];

    @observable
    scopes: ApiKeyScope[] = [];

    @observable
    projects: ProjectResult[] = [];

    @observable
    isLoading: boolean = false;

    @observable
    hasLoadError: boolean = false;

    @observable
    isSaving: boolean = false;

    @observable
    isEditorVisible: boolean = false;

    @observable
    editingApiKey: ApiKey | null = null;

    // Kept with the request state so navigating during create/regenerate cannot discard an unseen key.
    @observable
    rawKey: string | null = null;

    constructor(
        private readonly service: GlobalAdministrationService,
        private readonly projectsService: ProjectsService,
        private readonly usersStore: UsersStore
    ) {}

    @computed
    get isBusy() {
        return this.isLoading || this.isSaving;
    }

    getUserNameById(id: string) {
        return this.usersStore.getUserNameById(id);
    }

    @action.bound
    async initialize() {
        if (this.isBusy) {
            return;
        }

        this.isLoading = true;

        try {
            const [apiKeys, scopes, projects] = await Promise.all([
                this.service.getApiKeys(),
                this.service.getApiKeyScopes(),
                this.projectsService.getProjectsResult(null)
            ]);

            if (apiKeys.isErr() || scopes.isErr() || projects.isErr()) {
                runInAction(() => {
                    this.hasLoadError = true;
                });
                return;
            }

            runInAction(() => {
                this.apiKeys = apiKeys.value;
                this.scopes = scopes.value;
                this.projects = projects.value;
                this.hasLoadError = false;
            });
        } catch {
            runInAction(() => {
                this.hasLoadError = true;
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    @action.bound
    dispose() {
        this.closeEditor();

        // Keep the global store locked until its pending request finishes, even after leaving the page.
        if (this.isBusy) {
            return;
        }

        this.apiKeys = [];
        this.scopes = [];
        this.projects = [];
        this.hasLoadError = false;
    }

    @action.bound
    openCreateModal() {
        if (this.isBusy) {
            return;
        }

        this.editingApiKey = null;
        this.isEditorVisible = true;
    }

    @action.bound
    openEditModal(apiKey: ApiKey) {
        if (this.isBusy) {
            return;
        }

        this.editingApiKey = apiKey;
        this.isEditorVisible = true;
    }

    @action.bound
    closeEditor() {
        this.editingApiKey = null;
        this.isEditorVisible = false;
    }

    @action.bound
    clearRawKey() {
        this.rawKey = null;
    }

    @action.bound
    async createApiKey(input: CreateApiKeyInput): Promise<void> {
        if (this.isBusy) {
            return;
        }

        this.isSaving = true;

        try {
            const response = await this.service.createApiKey(input);

            if (response.isErr()) {
                message.error('Could not create API key. Try again.');
                return;
            }

            runInAction(() => {
                this.applyApiKey(response.value.apiKey);
                this.rawKey = response.value.rawKey;
                this.closeEditor();
            });
            message.success('API key created. Copy the key now; it will not be shown again.');
        } catch {
            message.error('Could not create API key. Try again.');
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    @action.bound
    async updateApiKey(id: string, input: ApiKeyInput): Promise<boolean> {
        if (this.isBusy) {
            return false;
        }

        this.isSaving = true;

        try {
            const response = await this.service.updateApiKey(id, input);

            if (response.isErr()) {
                const validationMessage = response.error.status === 400 ? response.error.data?.title : undefined;
                message.error(validationMessage || 'Could not update API key. Try again.');
                return false;
            }

            this.applyApiKey(response.value);
            this.closeEditor();
            message.success('API key updated.');
            return true;
        } catch {
            message.error('Could not update API key. Try again.');
            return false;
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    @action.bound
    async setEnabled(apiKey: ApiKey, enabled: boolean): Promise<void> {
        if (this.isBusy) {
            return;
        }

        this.isSaving = true;

        try {
            const response = await this.service.setApiKeyEnabled(apiKey.id, enabled);

            if (response.isErr()) {
                message.error(`Could not ${enabled ? 'enable' : 'disable'} API key. Try again.`);
                return;
            }

            this.applyApiKey(response.value);
            message.success(`API key ${enabled ? 'enabled' : 'disabled'}.`);
        } catch {
            message.error(`Could not ${enabled ? 'enable' : 'disable'} API key. Try again.`);
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    @action.bound
    async regenerate(apiKey: ApiKey): Promise<void> {
        if (this.isBusy) {
            return;
        }

        this.isSaving = true;

        try {
            const response = await this.service.regenerateApiKey(apiKey.id);

            if (response.isErr()) {
                message.error('Could not regenerate API key. Try again.');
                return;
            }

            runInAction(() => {
                this.applyApiKey(response.value.apiKey);
                this.rawKey = response.value.rawKey;
            });
            message.success('API key regenerated. Copy the new key now; it will not be shown again.');
        } catch {
            message.error('Could not regenerate API key. Try again.');
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    @action.bound
    async revoke(apiKey: ApiKey): Promise<void> {
        if (this.isBusy) {
            return;
        }

        this.isSaving = true;

        try {
            const response = await this.service.revokeApiKey(apiKey.id);

            if (response.isErr()) {
                message.error('Could not revoke API key. Try again.');
                return;
            }

            this.applyApiKey(response.value);
            message.success('API key revoked.');
        } catch {
            message.error('Could not revoke API key. Try again.');
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    @action
    private applyApiKey(apiKey: ApiKey) {
        const exists = this.apiKeys.some(value => value.id === apiKey.id);

        if (exists) {
            this.apiKeys = this.apiKeys.map(value => (value.id === apiKey.id ? apiKey : value));
            return;
        }

        this.apiKeys = [apiKey, ...this.apiKeys];
    }
}
